import { TIPOS_ACCESO, type TipoAcceso } from './enums.js';

/**
 * Matriz de permisos — §12.2 y §12.5.
 *
 * Los tres tipos de acceso de la especificación son la unidad que administra el
 * usuario final; los permisos granulares son la unidad que verifica el servidor.
 * Mantenerlos separados permite afinar una capacidad sin inventar un cuarto rol.
 */
export const PERMISOS = [
  // Órdenes de trabajo
  'ot:leer',
  'ot:crear',
  'ot:editar',
  'ot:eliminar',
  'ot:cambiar_estado',
  'ot:asignar',
  'ot:comentar',
  'ot:compartir_externo',

  // Solicitudes
  'solicitud:leer',
  'solicitud:leer_todas',
  'solicitud:crear',
  'solicitud:editar',
  'solicitud:resolver',
  'solicitud:eliminar',

  // Maestros de estructura física
  'ubicacion:leer',
  'ubicacion:escribir',
  'activo:leer',
  'activo:escribir',
  'parte:leer',
  'parte:escribir',
  'parte:transaccionar',

  // Maestros de trabajo
  'tipo_falla:leer',
  'tipo_falla:escribir',
  'procedimiento:leer',
  'procedimiento:escribir',
  'procedimiento:ejecutar',
  'medidor:leer',
  'medidor:escribir',
  'sujeto_evaluable:leer',
  'sujeto_evaluable:escribir',

  // Analítica
  'reporte:leer',
  'panel:leer',
  'panel:escribir',
  'exportacion:ejecutar',
  'importacion:ejecutar',

  // Administración de la organización
  'usuario:leer',
  'usuario:administrar',
  'grupo:leer',
  'grupo:administrar',
  'catalogo:administrar',
  // Listas de materiales y herramientas por tipo de activo y tipo de trabajo.
  'lista_materiales:editar',
  'campo_personalizado:administrar',
  'auditoria:leer',
  'licencia:leer',
  'licencia:administrar',
  'organizacion:administrar',

  /**
   * Versión mínima / mantenimiento de la app nativa (`platform.versiones_app`).
   * A propósito NO va en `PERMISOS_FULL_USER` ni `PERMISOS_SOLICITANTE`: es
   * configuración GLOBAL —afecta a todas las organizaciones, no solo la
   * propia—, así que solo ADMINISTRADOR la recibe (queda en `PERMISOS`
   * completo, que es lo que ese tipo de acceso recibe entero).
   */
  'app_version:gestionar',
] as const;

export type Permiso = (typeof PERMISOS)[number];

const PERMISOS_FULL_USER: readonly Permiso[] = [
  'ot:leer',
  'ot:crear',
  'ot:editar',
  'ot:cambiar_estado',
  'ot:asignar',
  'ot:comentar',
  'ot:compartir_externo',
  'solicitud:leer',
  'solicitud:leer_todas',
  'solicitud:crear',
  'solicitud:editar',
  'solicitud:resolver',
  'ubicacion:leer',
  'ubicacion:escribir',
  'activo:leer',
  'activo:escribir',
  'parte:leer',
  'parte:escribir',
  'parte:transaccionar',
  'tipo_falla:leer',
  'tipo_falla:escribir',
  'procedimiento:leer',
  'procedimiento:escribir',
  'procedimiento:ejecutar',
  'medidor:leer',
  'medidor:escribir',
  'sujeto_evaluable:leer',
  'sujeto_evaluable:escribir',
  'reporte:leer',
  'panel:leer',
  'panel:escribir',
  'exportacion:ejecutar',
  // TEMPORAL: liberado a pedido para que cualquier Full User (no solo
  // Administrador) vea y use el botón «Importar CSV» de activos/ubicaciones
  // mientras dura la migración desde GetMaintainX. Quitar de acá cuando ya
  // no haga falta — vuelve a quedar solo para ADMINISTRADOR automáticamente.
  'importacion:ejecutar',
  'usuario:leer',
  'grupo:leer',
  'auditoria:leer',
];

/**
 * El solicitante solo crea solicitudes y consulta las propias (§9.4, §12.5).
 * Nótese la ausencia deliberada de `solicitud:leer_todas`: el filtrado por autor
 * lo aplica el repositorio, no la interfaz.
 */
const PERMISOS_SOLICITANTE: readonly Permiso[] = ['solicitud:leer', 'solicitud:crear'];

export const PERMISOS_POR_TIPO_ACCESO: Record<TipoAcceso, readonly Permiso[]> = {
  ADMINISTRADOR: PERMISOS,
  FULL_USER: PERMISOS_FULL_USER,
  SOLICITANTE: PERMISOS_SOLICITANTE,
};

export function permisosDe(tipoAcceso: TipoAcceso): readonly Permiso[] {
  return PERMISOS_POR_TIPO_ACCESO[tipoAcceso];
}

export function tienePermiso(tipoAcceso: TipoAcceso, permiso: Permiso): boolean {
  return PERMISOS_POR_TIPO_ACCESO[tipoAcceso].includes(permiso);
}

/**
 * Tipos de acceso que consumen un asiento facturable.
 *
 * Regla de licenciamiento acordada: el precio por asiento se cobra en soles y
 * solo lo consumen Administrador y Full User. «Únicamente solicitante» es
 * gratuito, que es lo que hace viable desplegar el sistema en las 15 sedes.
 */
export const TIPOS_ACCESO_FACTURABLES = ['ADMINISTRADOR', 'FULL_USER'] as const satisfies readonly TipoAcceso[];

export function consumeAsiento(tipoAcceso: TipoAcceso): boolean {
  return (TIPOS_ACCESO_FACTURABLES as readonly TipoAcceso[]).includes(tipoAcceso);
}

/** Tipos de acceso gratuitos, derivados para que la lista no se duplique. */
export const TIPOS_ACCESO_GRATUITOS: readonly TipoAcceso[] = TIPOS_ACCESO.filter((t) => !consumeAsiento(t));
